import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Form, Input, Modal, Select } from 'antd';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { Icon } from './icons.tsx';
import { useAuth } from './auth.tsx';
import { createCustomer } from './customer-api.ts';
import { ApiRequestError, createProject, listProjectCreationOptions } from './project-api.ts';

interface Choice { value: string; label: string }
interface FormValues { name: string; customer: Choice; manager: Choice }

function ReferenceSelect({ kind, token, accountId, value, onChange }: {
  kind: 'customer' | 'manager'; token: string; accountId: string;
  value?: Choice; onChange?: (value: Choice) => void;
}) {
  const [search, setSearch] = useState('');
  const options = useInfiniteQuery({
    queryKey: ['project-creation-options', accountId, kind, search], initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => listProjectCreationOptions(token, kind, search, pageParam ?? undefined),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: !!token && !!accountId,
  });
  const rows = options.data?.pages.flatMap((page) => page.items) ?? [];
  return <Select showSearch labelInValue filterOption={false} value={value ?? null} onChange={(next) => { if (next) onChange?.(next as Choice); setSearch(''); }}
    onSearch={setSearch} placeholder={kind === 'customer' ? '搜索客户名称或编号' : '搜索负责人姓名或编号'}
    loading={options.isPending} notFoundContent={options.isPending ? '正在查询…' : options.isError ? '查询失败，请重试' : '没有匹配的有效资料'}
    options={rows.map((row) => ({ value: row.id, label: `${row.code} · ${row.name}` }))}
    popupRender={(menu) => <>{menu}{(options.hasNextPage || options.isError) && <Button className="parent-load-more" type="link"
      loading={options.isFetchingNextPage} onClick={() => void (options.isError && !options.data ? options.refetch() : options.fetchNextPage())}>
      {options.isError ? '重试查询' : '加载更多选项'}
    </Button>}</>} />;
}

function projectError(error: unknown): string {
  if (error instanceof ApiRequestError) {
    if (error.code === 'REFERENCE_NOT_FOUND') return '客户或负责人资料已失效，请重新选择。';
    if (error.code === 'FORBIDDEN') return '当前账号无创建项目权限，或权限已被撤销。';
    if (error.code === 'IDEMPOTENCY_PAYLOAD_CONFLICT') return '重试内容已变化，请再次提交。';
    return `项目创建失败（${error.code}），请核对资料后重试。`;
  }
  return error instanceof Error ? error.message : '项目创建失败，请稍后重试。';
}

export function CreateProjectPage() {
  const { state } = useAuth();
  const token = state.kind === 'ready' ? state.token : '';
  const accountId = state.kind === 'ready' ? state.session.userId : '';
  const personId = state.kind === 'ready' ? state.session.personId : '';
  const isAdmin = state.kind === 'ready' && state.session.roleAssignments.some((role) => role.capabilityBaseline === 'SYSTEM_ADMIN');
  const canCreate = state.kind === 'ready' && state.session.capabilities.includes('project.create');
  const canCreateCustomer = state.kind === 'ready' && state.session.capabilities.includes('customer.manage');
  const client = useQueryClient();
  const [form] = Form.useForm<FormValues>();
  const mutation = useMutation({ mutationFn: createProject });
  const customerMutation = useMutation({ mutationFn: ({ name, key }: { name: string; key: string }) =>
    createCustomer(token, { name, legalName: name, customerType: 'UNSPECIFIED', organization: null, department: null,
      status: 'PROSPECT', region: null, address: null, notes: null, responsiblePersonIds: [] }, key) });
  const attempt = useRef<{ payload: string; key: string } | null>(null);
  const [created, setCreated] = useState<{ id: string; code: string; accessible: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [quickCustomerOpen, setQuickCustomerOpen] = useState(false);
  const [quickCustomerName, setQuickCustomerName] = useState('');
  const quickCustomerAttempt = useRef<{ name: string; key: string } | null>(null);

  async function quickCreateCustomer() {
    const name = quickCustomerName.trim();
    if (!name) return;
    if (quickCustomerAttempt.current?.name !== name) quickCustomerAttempt.current = {
      name, key: `quick-customer-${crypto.randomUUID()}`,
    };
    setError(null);
    try {
      const result = await customerMutation.mutateAsync({ name, key: quickCustomerAttempt.current.key });
      form.setFieldValue('customer', { value: result.customerId, label: `${result.code} · ${result.name}` });
      quickCustomerAttempt.current = null; setQuickCustomerOpen(false); setQuickCustomerName('');
      await client.invalidateQueries({ queryKey: ['project-creation-options', accountId, 'customer'] });
    } catch (failure) { setError(projectError(failure)); }
  }

  async function submit(values: FormValues) {
    setError(null); setCreated(null);
    const draft = { name: values.name.trim(), businessEntityId: values.customer.value,
      primaryManagerId: values.manager.value };
    const payload = JSON.stringify(draft);
    if (attempt.current?.payload !== payload) attempt.current = { payload, key: `create-project-${crypto.randomUUID()}` };
    try {
      const result = await mutation.mutateAsync({ token, idempotencyKey: attempt.current.key, ...draft });
      setCreated({ id: result.projectId, code: result.projectCode,
        accessible: isAdmin || values.manager.value === personId });
      attempt.current = null;
      form.resetFields();
      await client.invalidateQueries({ queryKey: ['my-projects', accountId] });
    } catch (failure) { setError(projectError(failure)); }
  }

  return <main className="page-shell project-create-shell prototype-create-project">
    <Link className="back-link" to="/projects">← 返回项目</Link>
    {!canCreate ? <Alert type="error" showIcon title="当前账号无创建项目权限" description="请联系管理员配置业务、项目负责人或管理员角色。" /> : <>
      <div className="prototype-creation-layout"><section className="prototype-creation-intro">
        <span className="prototype-eyebrow">NEW PROJECT</span><h1>创建一个<br />干净的项目空间</h1>
        <p>只需最少信息即可建立项目。合同、团队和服务包都可在稍后补充。</p>
        <div className="prototype-creation-principles"><div><i><Icon name="check" size={14} /></i><span><b>允许空项目</b><small>暂无服务包不是错误状态</small></span></div>
          <div><i><Icon name="info" size={14} /></i><span><b>不会自动开始执行</b><small>服务包确认并启动后才产生任务</small></span></div></div>
      </section><section className="prototype-creation-form" aria-labelledby="new-project-title">
          <header><span>01</span><div><h2 id="new-project-title">基本信息</h2><p>这些信息将成为所有后续工作的上下文。</p></div></header>
          {created && <Alert className="workspace-alert" type="success" showIcon title={`${created.code} 已创建`}
            description={created.accessible ? <Link to={`/projects/${created.id}/contracts`}>进入项目合同档案 <Icon name="arrowRight" size={14} /></Link>
              : '项目已分配给指定负责人；你目前没有该项目的查看关系。'} />}
          {error && <Alert className="workspace-alert" type="error" showIcon title={error} closable onClose={() => setError(null)} />}
          <Form form={form} layout="vertical" onFinish={(values) => void submit(values)} onValuesChange={() => { setCreated(null); }}>
            <Form.Item name="name" label="项目名称" rules={[{ required: true, whitespace: true, message: '填写项目名称' }]}>
              <Input maxLength={120} placeholder="例如：客户年度内容运营" />
            </Form.Item>
            <Form.Item name="customer" label="客户" rules={[{ required: true, message: '选择有效客户' }]}>
              <ReferenceSelect kind="customer" token={token} accountId={accountId} />
            </Form.Item>
            {canCreateCustomer && <button type="button" className="quick-customer-link" onClick={() => {
              quickCustomerAttempt.current = null; setQuickCustomerName(''); setQuickCustomerOpen(true);
            }}><Icon name="plus" size={16} strokeWidth={2} /> 没有这位客户？先快速建档</button>}
            <Form.Item name="manager" label="项目负责人" rules={[{ required: true, message: '选择项目负责人' }]}>
              <ReferenceSelect kind="manager" token={token} accountId={accountId} />
            </Form.Item>
            <div className="prototype-generated-note"><i><Icon name="info" size={14} /></i><span><b>项目编号由系统生成</b><small>保存后项目进入“准备中”，不会自动创建服务包。</small></span></div>
            <footer className="project-create-actions"><Link className="prototype-button" to="/projects">取消</Link>
              <Button type="primary" htmlType="submit" loading={mutation.isPending}>创建项目 <Icon name="arrowRight" size={14} /></Button></footer>
          </Form>
        </section></div>
      <Modal title="快速新建客户" open={quickCustomerOpen} onCancel={() => setQuickCustomerOpen(false)}
        onOk={() => void quickCreateCustomer()} okText="创建并选中" cancelText="取消"
        confirmLoading={customerMutation.isPending} okButtonProps={{ disabled: !quickCustomerName.trim() }}>
        <div className="quick-customer-modal"><p>先建立一份待完善客户档案。系统会生成稳定客户 ID，联系人和开票资料可以稍后补充。</p>
          <label><span>客户显示名称 *</span><Input autoFocus maxLength={120} value={quickCustomerName}
            onChange={(event) => { setQuickCustomerName(event.target.value); quickCustomerAttempt.current = null; }}
            onPressEnter={() => void quickCreateCustomer()} placeholder="例如：云岭医院 · 心内科" /></label>
          <section><b>只建立客户主档</b><small>不会自动创建项目、合同、账号或费用；创建成功后会自动选中这位客户。</small></section></div>
      </Modal>
    </>}
  </main>;
}
